import { Async, Cast, Obj } from '@infiniteobjects/core-library-web'
import type { Nullable } from '@infiniteobjects/type-utils'
import { $global, attr, DOM, observable, volatile } from '@microsoft/fast-element'
import type { FoundationElementDefinition } from '@microsoft/fast-foundation'
import typia from 'typia'
import type { IVariant } from '../../common/entities'
import type { ShopifyResourceId } from '../../common/entities/common'
import type { VariantChangePayload } from '../../common/entities/events/variant-change'
import { EVENT_NAMES } from '../../constants'
import { captureAndLogError, captureAndLogMessage, updateHistoryState } from '../../utils'
import type { Dialog } from '../dialog/dialog.element'
import type { MediaItem } from '../media/media-item'
import { ProductConnectedElement } from '../product-connected'
enum IoMsgType {
  ping = 'ping',
  pong = 'pong',
  ready = 'app:ready',
  variantReady = 'variant:ready',
  variantData = 'variant:data',
  variantSelected = 'variant:selected',
  setCreationId = 'creation:id',
  addToCart = 'cart:add',
  modalOpen = 'modal:open',
}

interface IoBaseEvent<Type extends IoMsgType> {
  type: Type
}

interface IoBaseDataEvent<Type extends IoMsgType, Payload> extends IoBaseEvent<Type> {
  payload: Payload
}

type IoDataEvent =
  | IoBaseDataEvent<IoMsgType.variantData, IVariant[]>
  | IoBaseDataEvent<IoMsgType.variantSelected, ShopifyResourceId>
  | IoBaseDataEvent<IoMsgType.setCreationId, string>

type IoNoDataEvent =
  | IoBaseEvent<IoMsgType.ping>
  | IoBaseEvent<IoMsgType.pong>
  | IoBaseEvent<IoMsgType.ready>
  | IoBaseEvent<IoMsgType.variantReady>
  | IoBaseEvent<IoMsgType.modalOpen>
  | IoBaseEvent<IoMsgType.addToCart>

type IoEvent = IoDataEvent | IoNoDataEvent

/**
 * ProductUgc configuration options
 * @public
 */
export type ProductUgcDefinitionOptions = FoundationElementDefinition

/**
 * ProductUgc component
 *
 * @customElement `io-theme-product-ugc`
 *
 * @slot slotName - description
 * @csspart partName - description
 *
 * @public
 */
export class ProductUgc extends ProductConnectedElement {
  constructor() {
    super('ugc')
  }

  private lastEventPayload: Nullable<VariantChangePayload> = null

  @observable frame: HTMLIFrameElement
  @observable dialog: Dialog
  @observable slottedSizeChartElements: MediaItem[]
  @observable creationId: string | null = null
  @attr({ attribute: 'app-host' }) applicationHost: string

  /**
   * True if the product data is ready
   * @remarks
   * It is ready when:
   * - The product data is fetched and parsed
   * - There is a selected variant
   * - There was no error fetching or parsing the product data
   */
  @volatile
  get ready(): boolean {
    return !!(this.product && this.selectedVariant && this.errored === false && this.applicationHost)
  }

  @volatile
  get eventPayload(): Nullable<VariantChangePayload> {
    if (!this.product || !this.selectedVariant) return null

    // construct the payload from the parsed internal objects
    return {
      productId: this.product.id,
      variantId: this.selectedVariant.id,
    }
  }

  // Overrude for variantId
  override variantIdChanged(oldValue: number, newValue: number): void {
    super.variantIdChanged(oldValue, newValue)
    if (oldValue === newValue) return
    if (!this.ready) return
    this.sendMessage({ type: IoMsgType.variantSelected, payload: this.variantId })
    this.emitVariantChangeEvent()
    updateHistoryState('variant', Cast.asString(this.variantId))
  }

  /**
   * Emits the variant change event from the window and the component
   */
  private emitVariantChangeEvent(): void {
    if (!this.eventPayload) return this.debug('Cannot emit variant change event, missing selected variant')
    if (this.lastEventPayload && Obj.equals(this.lastEventPayload, this.eventPayload))
      return this.debug('Event payload is the same, skipping')

    const payload: VariantChangePayload = Obj.clone(this.eventPayload) // Clone to prevent mutation
    this.lastEventPayload = payload // Store the last payload to prevent emitting the same event twice

    this.debug('Emitting variant change event %o', payload)

    // Emit the event from the component
    this.$emit('variant-change', payload)

    // Emit the event from the window
    const type = EVENT_NAMES.variantChange
    const data = { detail: payload }
    window.dispatchEvent(new CustomEvent<VariantChangePayload>(type, data))
  }

  /**
   * Log when the component is connected
   */
  connectedCallback(): void {
    super.connectedCallback()
    this.debug('connected')
    $global.addEventListener('message', this.receiveMessage)
    DOM.queueUpdate(() => this.frame?.contentWindow?.localStorage.setItem('debug', 'io:*'))
  }

  /**
   * Log when the component is disconnected
   */
  disconnectedCallback(): void {
    this.debug('disconnected')
    $global.removeEventListener('message', this.receiveMessage)
    super.disconnectedCallback()
  }

  receiveMessage = (evt: MessageEvent): void => {
    const event = evt.data

    if (!typia.is<IoEvent>(event)) return

    this.logEvent(event, 'receive')

    switch (event.type) {
      case IoMsgType.ping:
        this.sendMessage({ type: IoMsgType.pong })
        break
      case IoMsgType.ready:
        this.sendMessage({ type: IoMsgType.variantData, payload: this.product.variants })
        break
      case IoMsgType.variantReady:
        this.sendMessage({ type: IoMsgType.variantSelected, payload: this.selectedVariant.id })
        break
      case IoMsgType.setCreationId:
        this.creationId = event.payload
        break
      case IoMsgType.variantSelected:
        this.variantId = event.payload
        break
      case IoMsgType.addToCart:
        this.submitForm()
        break
      case IoMsgType.modalOpen:
        this.handleOpenModal()
        break
    }
  }

  sendMessage = (event: IoEvent): void => {
    this.logEvent(event, 'send')
    this.frame?.contentWindow?.postMessage(event, this.applicationHost)
  }

  private logEvent(event: IoEvent, direction: 'send' | 'receive'): void {
    const directionStr = direction === 'send' ? '→' : '←'
    if ('payload' in event) {
      return this.debug(`event ${directionStr} %s`, event.type, { payload: event.payload })
    }
    this.debug(`event ${directionStr} %s`, event.type)
  }

  private getFormData(): FormData {
    const form = new FormData()
    // Add the form type
    form.set('form_type', 'product')
    // Add the utf8 key
    form.set('utf8', '✓')
    // Add the product id
    form.set('product-id', Cast.asString(this.productId))
    // Add the variant id
    form.set('id', Cast.asString(this.selectedVariant.id))
    // Add the quantity
    form.set('quantity', '1')
    // Add the properties
    // Add ships in (if available)
    if (this.selectedVariant.shipping.shipsIn)
      form.set('properties[Ships]', this.selectedVariant.shipping.shipsIn)
    // Add the Creation ID
    if (this.creationId) form.set('properties[creationId]', this.creationId)
    // Return the form
    return form
  }

  private submitForm(): void {
    const formData = this.getFormData()

    fetch($global.window.ioThemeSettings.routes.cartAdd, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/javascript',
      },
      body: formData,
    })
      .then((response) => response.json())
      .then(Async.tap((response) => this.debug('Response: %o', response)))
      .then((response) => {
        if (response.status) {
          captureAndLogMessage(response.message, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: response.errors || response.description,
          })

          return
        }

        this.debug('Redirecting to cart: %s', window.ioThemeSettings.routes.cart)
        window.location.assign(window.ioThemeSettings.routes.cart)
      })
      .catch((err) => {
        captureAndLogError(err, { source: 'ugc-form' })
      })
  }

  handleOpenModal(): void {
    if (!this.dialog) return
    this.dialog.open()
  }
}
